import React, { useState, useRef, useEffect } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, Send, Loader2, Bot, User } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How much money did we receive today?",
  "Is month PCC income kitni hai?",
  "Agency ko total kitna advance diya hua hai?",
  "Today cash closing balance kya hai?",
  "Jamnagar aur Porbandar ka expense compare karo",
  "Which expense category increased this month?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Namaste! I'm your accounting assistant. Ask me anything about Prabhat's finances — in English, Hindi or Hinglish." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (q) => {
    const question = q || input;
    if (!question.trim()) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/query", { question });
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't process that right now." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-140px)]">
      <PageHeader title="AI Assistant" subtitle="Ask about your finances — answers use verified system data only" />
      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${m.role}`}>
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-slate-200 dark:bg-slate-700" : "bg-teal-500/15"}`}>
              {m.role === "user" ? <User className="h-4 w-4 text-slate-600 dark:text-slate-300" /> : <Bot className="h-4 w-4 text-teal-600" />}
            </div>
            <div className={`rounded-xl px-4 py-2.5 text-sm max-w-[80%] ${m.role === "user" ? "bg-[#0F2942] text-white" : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200"}`}>
              <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
            </div>
          </div>
        ))}
        {loading && <div className="flex gap-3"><div className="h-8 w-8 rounded-lg bg-teal-500/15 flex items-center justify-center"><Bot className="h-4 w-4 text-teal-600" /></div>
          <div className="rounded-xl px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div></div>}
        <div ref={endRef} />
      </div>
      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => send(s)} data-testid="ai-suggestion" className="text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full px-3 py-1.5 text-slate-600 dark:text-slate-300 hover:border-teal-400">{s}</button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about income, expenses, advances, balances..." className="h-11" />
        <Button data-testid="ai-send" onClick={() => send()} disabled={loading} className="bg-teal-600 hover:bg-teal-700 h-11 px-4"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
